import assert from 'assert-ts';
import type {Schemas} from 'domainTypes/dto';
import {Concept} from 'types';
import type {
  VerifiedLinkedAgentLink,
  VerifiedLinkedValueLink,
} from 'schemaDefinition/types';
import {getId} from 'services/utils/functions/getId';
import {CodeListRef} from 'schemas/references';
import {type Prize, type PrizeWinner} from '../types';

export const mapGetPrizeDto = ({
  presenter: loosePresenter,
  startYear,
  endYear,
  winners,
  yearsWithoutWinners,
  ...dto
}: Schemas.LiteraturePrizeWithWinners): Prize => {
  return {
    ...dto,
    month: dto.month ? dto.month.toString() : undefined,
    startYear: startYear ?? undefined,
    endYear: endYear ?? undefined,
    presenter: loosePresenter ?? '',
    winners: [
      ...(winners ?? []).map(mapPrizeWinnerDto),
      ...(yearsWithoutWinners ?? []).map(mapNoWinner),
    ].sort(comparePrizeWinners),
  };
};

const mapNoWinner = (year: number): PrizeWinner => ({
  id: getId(),
  year,
  type: CodeListRef.PRIZE_TYPE.None,
});

export const mapToCreatePrizeDto = ({
  winners,
  ...prize
}: Prize): Schemas.CreateLiteraturePrizeRequest => {
  return {
    ...prize,
    month:
      prize.month && !Number.isNaN(prize.month)
        ? parseInt(prize.month, 10)
        : undefined,
    ...mapPrizeWinnersToPrize(winners ?? []),
  };
};

export const mapToUpdatePrizeDto = ({
  winners,
  ...prize
}: Prize): Schemas.UpdateLiteraturePrizeRequest => {
  return {
    ...prize,
    month:
      prize.month && !Number.isNaN(prize.month)
        ? parseInt(prize.month, 10)
        : undefined,
    ...mapPrizeWinnersToPrize(winners ?? []),
  };
};

const mapPrizeWinnerDto = ({
  type: looseType,
  year: looseYear,
  agent: looseAgent,
  work,
  ...dto
}: Schemas.LiteraturePrizeWinner): PrizeWinner => {
  const type = assert(looseType, 'mapPrizeWinnerDto: type must be defined');
  const year = assert(looseYear, 'mapPrizeWinnerDto: year must be defined');
  const agent = assert(looseAgent, 'mapPrizeWinnerDto: agent must be defined');

  return {
    ...dto,
    type,
    year,
    agent: {link: mapLiteraturePrizeAgent(agent)},
    work: work ? {link: mapLiteraturePrizeWork(work)} : undefined,
  };
};

export const mapLiteraturePrizeAgent = (
  dto: Schemas.LiteraturePrizeAgent,
): VerifiedLinkedAgentLink => {
  return {
    linkStatus: 'verified',
    type: Concept.person,
    entityId: dto.id,
    agentName: {
      name: dto.name,
      surName: dto.surName,
    },
  };
};

export const mapLiteraturePrizeWork = (
  dto: Schemas.LiteraturePrizeWork,
): VerifiedLinkedValueLink => {
  return {
    linkStatus: 'verified',
    entityId: dto.id,
    type: Concept.work,
    name: dto.title,
  };
};

export const mapLiteraturePrizeLink = (
  dto: Schemas.LiteraturePrize,
): VerifiedLinkedValueLink => {
  return {
    linkStatus: 'verified',
    entityId: dto.id,
    type: Concept.prize,
    name: dto.name,
  };
};

const mapPrizeWinnersToPrize = (
  prizeWinners: PrizeWinner[],
): Pick<
  Schemas.CreateLiteraturePrizeRequest | Schemas.UpdateLiteraturePrizeRequest,
  'winners' | 'yearsWithoutWinners'
> => {
  const yearsWithoutWinners: number[] = [];
  const winners: Schemas.CreateLiteraturePrizeWinner[] = [];

  for (const prizeWinner of prizeWinners) {
    // If type is None (Ikke tildelt), just grab the year and
    // put it in a separate array
    if (prizeWinner.type === CodeListRef.PRIZE_TYPE.None) {
      yearsWithoutWinners.push(prizeWinner.year);
    } else {
      winners.push({
        ...prizeWinner,
        agentId: assert(
          prizeWinner.agent.link.entityId,
          'mapped agent must have entityId',
        ),
        workId: prizeWinner.work
          ? assert(
              prizeWinner.work.link.entityId,
              'mapped work must have entityId',
            )
          : undefined,
      });
    }
  }

  // Remove yearWithoutWinner with same year as a winner
  for (const prizeWinner of winners) {
    const i = yearsWithoutWinners.indexOf(prizeWinner.year);
    if (i !== -1) {
      yearsWithoutWinners.splice(i, 1);
    }
  }

  return {
    yearsWithoutWinners,
    winners,
  };
};

const prizeTypeSortValue: Record<PrizeWinner['type'], number> = {
  AUTHORSHIP: 1,
  AUTHOR: 2,
  ILLUSTRATOR: 3,
  TRANSLATOR: 4,
  NARRATOR: 5,
  NONE: 5,
};

const comparePrizeWinners = (a: PrizeWinner, b: PrizeWinner) => {
  // By year descending
  if (a.year !== b.year) {
    return (b.year ?? 0) - (a.year ?? 0);
  }

  // By type
  return prizeTypeSortValue[a.type ?? 0] - prizeTypeSortValue[b.type ?? 0];
};
